/**
 * 统一权限决策引擎
 *
 * 基于后端权限树 + 当前用户信息 + 业务数据 三个维度综合判断，
 * 为前端 UI 提供标准化的 { visible, disabled, tooltip } 决策结果。
 *
 * 权限优先级：
 * 1. super_admin 短路（持有全部权限）
 * 2. 后端返回的用户权限列表（最权威）
 * 3. 角色硬编码兜底表（权限列表未下发时，见 utils/permissions.hasPermissionByRole）
 */
import { computed } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { toPermissionRole, hasPermissionByRole, PERMISSION_CODES } from '@/utils/permissions'
import type { Ledger } from '@/types/ledger'
import type { WorkRecord } from '@/types/work-record'

// ============================================================
// 业务条件类型定义
// ============================================================

export interface BusinessConditions {
  /** 台账是否处于可写状态（非 pending_review/approved/archived） */
  isWritable?: boolean
  /** 当前用户是否为台账负责人 */
  isOwner?: boolean
  /** 当前用户是否为台账成员 */
  isMember?: boolean
  /** 台账状态 */
  status?: string
  /** 工作记录的记录人 ID */
  recorderId?: string
  /** 额外自定义条件 */
  [key: string]: unknown
}

/** 统一权限决策结果 */
export interface PermissionDecision {
  /** 是否可见 */
  visible: boolean
  /** 是否禁用（可见但不可用） */
  disabled: boolean
  /** 禁用时的 tooltip 提示文案 */
  tooltip?: string
}

// ============================================================
// 工具函数
// ============================================================

/** 获取只读状态的 tooltip 文案 */
function getReadOnlyTip(status: string | undefined): string {
  const map: Record<string, string> = {
    pending_review: '该台账正在审核中，',
    approved: '该台账已审核通过，',
    archived: '该台账已归档，',
  }
  return (map[status ?? ''] ?? '') + '不可操作'
}

/** 判断是否为管理层角色 */
function isManagerOrLeader(role: string | undefined | null): boolean {
  const r = toPermissionRole(role ?? '')
  return r === 'manager' || r === 'leader' || r === 'admin'
}

// ============================================================
// 主 Composable
// ============================================================

export function useUnifiedPermission() {
  const { state } = useAuth()
  const user = computed(() => state.currentUser)

  /**
   * 检查用户是否拥有指定权限码
   *
   * 【修复角色权限三审#4】删除原「降级#2：权限码出现在权限树即视为持有」分支——
   * permissionGuard 为所有 admin 角色预载的是全量权限目录（系统里存在哪些权限），
   * 不代表当前用户持有它；该分支使 SYS_ADMIN 等角色的 v-permission/按钮门控恒真。
   * 真实持有口径 = 后端下发的 user.permissions，缺省时才落到角色兜底表。
   */
  function hasPermission(code: string): boolean {
    const u = user.value
    if (!u) return false
    if (u.role === 'super_admin') return true

    // 优先使用后端返回的权限列表
    const userPerms = u.permissions
    if (userPerms?.includes(code)) return true

    // 兜底：角色硬编码（仅权限列表未下发时）
    return hasPermissionByRole(u.role, code)
  }

  /**
   * 组合权限判断：权限码 + 业务条件
   * 返回标准化的 { visible, disabled, tooltip } 决策结果
   */
  function assessPermission(
    permissionCode: string,
    conditions?: BusinessConditions
  ): PermissionDecision {
    const u = user.value
    if (!u) return { visible: false, disabled: false }

    const hasPerm = hasPermission(permissionCode)

    // 完全无权限 → 隐藏
    if (!hasPerm) {
      return { visible: false, disabled: false }
    }

    // 有权限，检查业务条件
    if (conditions) {
      const { isWritable, isOwner, isMember, recorderId } = conditions

      // 只读状态 → 大部分写操作禁用
      if (isWritable === false) {
        return {
          visible: true,
          disabled: true,
          tooltip: getReadOnlyTip(conditions.status),
        }
      }

      // 修复台账审查#12：成员与负责人同等可操作（口径对齐后端 OrgAccessGuard.requireLedgerWritable
      // 「负责人、成员或审核人」），此前只认 isOwner，台账成员看不到提审/上传入口
      if (isOwner === false && isMember !== true && !isManagerOrLeader(u.role)) {
        return {
          visible: true,
          disabled: true,
          tooltip: '仅台账负责人或成员可执行此操作',
        }
      }

      // 工作记录编辑/删除：非记录人且非管理者
      if (recorderId && !isManagerOrLeader(u.role) && recorderId !== u.id) {
        return {
          visible: true,
          disabled: true,
          tooltip: '仅记录人本人可执行此操作',
        }
      }
    }

    // 有权限 + 满足业务条件 → 可用
    return { visible: true, disabled: false }
  }

  /**
   * 便捷方法：台账相关权限判断
   */
  function forLedger(
    ledger: Ledger | undefined | null,
    action: 'view' | 'edit' | 'delete' | 'review' | 'submit' | 'analyze' | 'archive' | 'assignTask' | 'uploadMaterial' | 'editStructure'
  ): PermissionDecision {
    if (!ledger || !user.value) {
      return { visible: false, disabled: false }
    }

    const isReadonly = ['pending_review', 'approved', 'archived'].includes(ledger.status)
    const isOwner = ledger.ownerId === user.value!.id
    const isMember = ledger.memberIds?.includes(user.value!.id)

    const conditions: BusinessConditions = {
      isWritable: !isReadonly,
      isOwner,
      isMember,
      status: ledger.status,
    }

    // 修复审查#11：权限码一律取 PERMISSION_CODES 常量（散写字面量会与后端实发码漂移且无从校验）
    const permCodeMap: Record<string, string> = {
      view: PERMISSION_CODES.LEDGER_VIEW,
      edit: PERMISSION_CODES.LEDGER_EDIT,
      delete: PERMISSION_CODES.LEDGER_DELETE,
      review: PERMISSION_CODES.LEDGER_AUDIT,
      // 修复台账审查#12：提审自有码 workspace:ledger:submit（后端 RolePresetConfig 授予
      // manager/leader/staff），此前误用 LEDGER_CREATE 使 STAFF（持 submit 无 create）看不到提审入口
      submit: PERMISSION_CODES.LEDGER_SUBMIT,
      analyze: PERMISSION_CODES.WORKSPACE_ANALYSIS_VIEW,
      // 归档端点后端消费 workspace:ledger:delete（无独立 archive 码）
      archive: PERMISSION_CODES.LEDGER_DELETE,
      assignTask: PERMISSION_CODES.LEDGER_CREATE,
      uploadMaterial: PERMISSION_CODES.RECORD_CREATE,
      editStructure: PERMISSION_CODES.LEDGER_EDIT,
    }

    const code = permCodeMap[action] || PERMISSION_CODES.LEDGER_VIEW
    return assessPermission(code, conditions)
  }

  /**
   * 便捷方法：工作记录相关权限判断
   */
  function forWorkRecord(
    record: WorkRecord | undefined | null,
    action: 'edit' | 'delete'
  ): PermissionDecision {
    if (!record || !user.value) {
      return { visible: false, disabled: false }
    }

    return assessPermission(
      action === 'edit' ? PERMISSION_CODES.RECORD_EDIT : PERMISSION_CODES.RECORD_DELETE,
      { recorderId: record.recorderId }
    )
  }

  return {
    state,
    user,
    hasPermission,
    assessPermission,
    forLedger,
    forWorkRecord,
    // 便捷计算属性（用于 Dashboard 等页面）
    isManager: computed(() => {
      const r = toPermissionRole(user.value?.role ?? '')
      return r === 'manager'
    }),
    isLeader: computed(() => {
      const r = toPermissionRole(user.value?.role ?? '')
      return r === 'leader'
    }),
    canViewCharts: computed(() => {
      const r = toPermissionRole(user.value?.role ?? '')
      return r === 'leader'
    }),
  }
}
